/**
 * Состояние вкладки/связи во время клиентского запроса.
 *
 * Вынесено из api/client/apiCallWrapper отдельным модулем без зависимостей —
 * чтобы логику отсечки шума можно было покрыть тестом (клиентские эффекты в
 * локальном dev не проверить).
 *
 * Зачем это нужно: на мобильных блокировка экрана, сворачивание браузера или
 * переключение Wi-Fi ↔ LTE подвешивают уже отправленный XHR. Ошибки сокет не
 * отдаёт, ответ не приходит, и по истечении таймаута запрос рвётся сам. В
 * GlitchTip это выглядит как «бэкенд не ответил», хотя бэкенд в этот момент
 * отвечает нормально — соседние запросы той же страницы возвращают 200.
 */

/** Когда вкладку в последний раз скрывали (мс). 0 — не скрывали ни разу. */
let lastHiddenAt = 0;
let watching = false;

/**
 * Во сколько раз фактическое время запроса должно превысить его таймаут,
 * чтобы считать вкладку замороженной.
 *
 * iOS может усыпить страницу, не успев доставить ни одного события о скрытии:
 * JS замирает, а ошибка таймаута всплывает только после разблокировки. Отличить
 * такой случай можно по часам: браузер рвёт запрос по таймауту (20 с), а между
 * стартом и обработкой ошибки проходят минуты. Реальное зависание бэкенда так
 * не выглядит — там время запроса упирается ровно в таймаут.
 *
 * Пример из GlitchTip: страница загрузилась в 18:46:01, ошибка
 * incrementProductViews пришла в 18:50:06 — 4 минуты при таймауте 20 с, и за
 * всё это время ни одного другого запроса со страницы.
 */
const FROZEN_TAB_FACTOR = 1.5;

function getDocument(): Document | undefined {
  return typeof document === "undefined" ? undefined : document;
}

function isHiddenNow(): boolean {
  return getDocument()?.visibilityState === "hidden";
}

function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Начать следить за уходом вкладки в фон. Вызывается лениво из
 * wasClientUnreachable — отдельно дёргать не нужно.
 *
 * Слушаем не только visibilitychange: при сворачивании приложения и блокировке
 * экрана iOS чаще отдаёт pagehide, а Chrome перед заморозкой — freeze.
 */
function watchBackgrounding(): void {
  const doc = getDocument();
  if (watching || !doc || typeof doc.addEventListener !== "function") {
    return;
  }
  watching = true;

  const markHidden = () => {
    lastHiddenAt = Date.now();
  };

  doc.addEventListener("visibilitychange", () => {
    if (doc.visibilityState === "hidden") {
      markHidden();
    }
  });
  doc.addEventListener("freeze", markHidden);

  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("pagehide", markHidden);
  }
}

export interface ClientRequestTiming {
  /** Date.now() на момент старта запроса. */
  startedAt: number;
  /** Сколько запрос реально шёл до ошибки. */
  elapsedMs: number;
  /** Таймаут, с которым запрос был отправлен. */
  timeoutMs: number;
}

/**
 * Запрос не мог доехать не по вине бэкенда: клиент оффлайн, вкладка скрыта
 * сейчас, её скрывали пока запрос шёл, либо вкладку явно замораживали
 * (см. FROZEN_TAB_FACTOR).
 */
export function wasClientUnreachable(timing: ClientRequestTiming): boolean {
  watchBackgrounding();

  if (isOffline() || isHiddenNow() || lastHiddenAt >= timing.startedAt) {
    return true;
  }

  return timing.elapsedMs > timing.timeoutMs * FROZEN_TAB_FACTOR;
}

/** Сброс состояния между тестами. В приложении не используется. */
export function resetClientContextForTests(): void {
  lastHiddenAt = 0;
  watching = false;
}
